import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { getBookings, submitBookingReview } from "../../services/booking.service";

const normalizePhone = (value) => {
  const digits = String(value || "").replace(/\D/g, "");
  if (!digits) return "";
  return digits.length > 10 ? digits.slice(-10) : digits;
};

const getBusinessUuidFromPath = (pathname = "") => {
  const parts = String(pathname || "").split("/").filter(Boolean);
  const storeIndex = parts.indexOf("store");
  if (storeIndex >= 0 && parts[storeIndex + 1]) {
    const maybeUuid = parts[storeIndex + 1];
    const reserved = new Set([
      "orders",
      "cart",
      "profile",
      "wishlist",
      "select-language",
      "invoice",
      "products",
      "product",
      "booking",
    ]);

    if (!reserved.has(maybeUuid)) {
      return maybeUuid;
    }
  }
  return "";
};

const formatDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString();
};

const canReviewBooking = (booking, customerProfile) => {
  if (!booking || !customerProfile || customerProfile.isGuest) return false;
  const status = String(booking.status || "").toLowerCase();
  if (status !== "completed") return false;
  return !booking.reviewSubmittedAt;
};

export default function GlobalServiceReviewPrompt() {
  const location = useLocation();
  const params = useParams();

  const [bookings, setBookings] = useState([]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState({ isOpen: false, booking: null });
  const [form, setForm] = useState({ rating: 5, title: "", comment: "" });
  const [submitting, setSubmitting] = useState(false);

  const pathname = location?.pathname || "";
  const isStorePage = pathname.includes("/store") || ["/orders", "/cart", "/profile", "/wishlist"].includes(pathname);
  const isOrderHistoryPage = pathname.includes("/store/orders") || pathname === "/orders";

  const businessUuid = useMemo(() => {
    return (
      params?.bussinessUuid ||
      getBusinessUuidFromPath(pathname) ||
      sessionStorage.getItem("activeBusinessUuid") ||
      sessionStorage.getItem("subdomainBusinessUuid") ||
      ""
    );
  }, [params?.bussinessUuid, pathname]);

  useEffect(() => {
    if (!businessUuid || !isStorePage) {
      setProfile(null);
      return;
    }

    const savedUser = localStorage.getItem(`user_${businessUuid}`);
    const savedGuest = localStorage.getItem(`guest_${businessUuid}`);

    if (savedUser) {
      try {
        const parsed = JSON.parse(savedUser);
        if (parsed) {
          setProfile({ ...parsed, isGuest: false });
          return;
        }
      } catch (error) {
        console.error("Failed to parse customer user profile", error);
      }
    }

    if (savedGuest) {
      try {
        const parsed = JSON.parse(savedGuest);
        if (parsed) {
          setProfile({ ...parsed, isGuest: true });
          return;
        }
      } catch (error) {
        console.error("Failed to parse customer guest profile", error);
      }
    }

    setProfile(null);
  }, [businessUuid, isStorePage]);

  useEffect(() => {
    if (!isStorePage || !businessUuid || !profile || profile?.isGuest) {
      setBookings([]);
      return;
    }

    const fetchBookings = async () => {
      setLoading(true);
      try {
        const mobile = normalizePhone(
          profile?.customer_mobile || profile?.phone || profile?.mobile || ""
        );
        const email = String(profile?.email || "").trim().toLowerCase();
        const name = String(profile?.name || profile?.customerName || "").trim();

        const filters = {};
        if (mobile) filters.mobile = mobile;
        if (email) filters.email = email;
        if (name) filters.name = name;

        const response = await getBookings(businessUuid, filters);
        const rows = Array.isArray(response) ? response : [];
        setBookings(rows);
      } catch (error) {
        console.error("Failed to load global booking review list", error);
        setBookings([]);
      } finally {
        setLoading(false);
      }
    };

    fetchBookings();
  }, [businessUuid, profile, isStorePage]);

  const pendingBookings = useMemo(
    () => bookings.filter((booking) => canReviewBooking(booking, profile)),
    [bookings, profile]
  );

  useEffect(() => {
    if (!isStorePage || isOrderHistoryPage || modal.isOpen) return;
    if (!pendingBookings.length) return;

    const firstPending = pendingBookings[0];
    const dismissedKey = `service_review_popup_dismissed_${firstPending._id}`;
    if (sessionStorage.getItem(dismissedKey) === "1") {
      return;
    }

    setForm({ rating: 5, title: "", comment: "" });
    setModal({ isOpen: true, booking: firstPending });
  }, [pendingBookings, isStorePage, isOrderHistoryPage, modal.isOpen]);

  const closeModal = () => {
    if (submitting) return;
    if (modal?.booking?._id) {
      sessionStorage.setItem(`service_review_popup_dismissed_${modal.booking._id}`, "1");
    }
    setModal({ isOpen: false, booking: null });
  };

  const handleSubmit = async () => {
    if (!businessUuid || !modal?.booking?._id) return;
    const comment = String(form.comment || "").trim();
    if (comment.length < 5) {
      toast.error("Please enter at least 5 characters in your review.");
      return;
    }

    setSubmitting(true);
    try {
      const customerId = profile?.customerId || profile?._id || profile?.id || "";
      const response = await submitBookingReview(businessUuid, modal.booking._id, {
        rating: form.rating,
        title: form.title,
        comment,
        customerId,
      });

      const updatedBooking = response?.booking;
      if (updatedBooking?._id) {
        setBookings((prev) =>
          prev.map((booking) =>
            booking._id === updatedBooking._id ? updatedBooking : booking
          )
        );
      }

      toast.success("Thanks for sharing your service experience.");
      closeModal();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to submit service review.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isStorePage || !businessUuid || !profile || profile?.isGuest) return null;

  return (
    <>
      {!isOrderHistoryPage && pendingBookings.length > 0 && !loading && (
        <button
          type="button"
          onClick={() => {
            const booking = pendingBookings[0];
            setForm({ rating: 5, title: "", comment: "" });
            setModal({ isOpen: true, booking });
          }}
          className="fixed bottom-5 right-5 z-[105] rounded-full bg-[#1a5fb0] px-4 py-2 text-sm font-semibold text-white shadow-lg hover:bg-[#164f92]"
        >
          Review pending ({pendingBookings.length})
        </button>
      )}

      {modal.isOpen && modal.booking && (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60 p-4"
          onClick={closeModal}
        >
          <div
            className="w-full max-w-lg rounded-2xl bg-white p-5"
            onClick={(event) => event.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-slate-900">How was your service experience?</h3>
            <p className="mt-1 text-sm text-slate-600">
              {modal.booking?.serviceName || "Service"} on {formatDate(modal.booking?.date)} at {modal.booking?.time}
            </p>

            <div className="mt-4 flex gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setForm((prev) => ({ ...prev, rating: star }))}
                  className={`text-3xl ${star <= form.rating ? "text-yellow-500" : "text-gray-300"}`}
                >
                  ★
                </button>
              ))}
            </div>

            <input
              type="text"
              value={form.title}
              onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))}
              placeholder="Title (optional)"
              className="mt-4 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            />

            <textarea
              rows={4}
              value={form.comment}
              onChange={(event) => setForm((prev) => ({ ...prev, comment: event.target.value }))}
              placeholder="Write your review..."
              className="mt-3 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            />

            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={closeModal}
                className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700"
              >
                Later
              </button>
              <button
                type="button"
                disabled={submitting}
                onClick={handleSubmit}
                className="rounded-lg bg-[#1a5fb0] px-4 py-1.5 text-sm font-semibold text-white disabled:opacity-70"
              >
                {submitting ? "Submitting..." : "Submit review"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}